/**
 * A labelled input.
 *
 * The label sits above the field and stays there. Material's floating label
 * saves a line of space and costs you the ability to read the form at a
 * glance — once every field is filled, every label is a 10px caption jammed
 * against the box above it. On a settings page with eighteen panels that is
 * the difference between scanning and reading.
 *
 * Headless UI's Field wires the label, description and error to the control
 * with the right `id`/`aria-describedby` for us, which is the whole reason to
 * use it rather than a bare `<label>`: those associations are what make the
 * error announce itself when focus lands on the box.
 */

import { forwardRef } from "react";
import {
  Description,
  Field as HeadlessField,
  Input,
  Label,
  Textarea,
} from "@headlessui/react";
import styles from "./Field.module.css";

export const Field = forwardRef(function Field(
  {
    label,
    hint,
    error,
    rows,
    prefix,
    suffix,
    className = "",
    containerClassName = "",
    ...rest
  },
  ref,
) {
  const Control = rows ? Textarea : Input;

  return (
    <HeadlessField className={`${styles.field} ${containerClassName}`.trim()}>
      {label && <Label className={styles.label}>{label}</Label>}

      {/* `data-multiline` hands the height back to the textarea: a field is on
          the control scale, and a thing sized by `rows` cannot be. */}
      <div
        className={styles.shell}
        data-invalid={error ? "" : undefined}
        data-multiline={rows ? "" : undefined}
      >
        {prefix && <span className={styles.affix}>{prefix}</span>}
        <Control
          ref={ref}
          rows={rows}
          invalid={Boolean(error)}
          className={`${styles.control} ${className}`.trim()}
          {...rest}
        />
        {suffix && <span className={styles.affix}>{suffix}</span>}
      </div>

      {/* The error replaces the hint rather than stacking under it: two lines
          of small text under a box is where people stop reading either. */}
      {error ? (
        <Description className={styles.error}>{error}</Description>
      ) : (
        hint && <Description className={styles.hint}>{hint}</Description>
      )}
    </HeadlessField>
  );
});
